import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AiFillFilePdf } from "react-icons/ai";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { addCourse, editCourse, getSingleCourse } from "../../redux/services/courseSlice";
import { ROUTES } from "../../constant";
import DescriptionEditor from "../Blog/DescriptionEditor";
import { CgRemove } from "react-icons/cg";
import { BiPlusCircle } from "react-icons/bi";

const CourseForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const getsingleCourseeData = useSelector((state) => state.course.singleCourseData);

  const queryParams = new URLSearchParams(location.search);
  const courseId = queryParams.get("courseId");

  const [loading, setLoading] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [preview, setPreview] = useState(null);
  const [description, setDescription] = useState(getsingleCourseeData?.description);
  const [metaDescription, setMetaDescription] = useState([""]);

  const initialValues = {
    title: "",
    image: "",
    language: "",
    syllabus: {
      subjectLevel: "",
      fullSyllabus: "",
    },
    courseMoneyBack: "",
    // totalLecture: 0,
    // totalTest: 0,
    description: "",
    pdf: "",
    price: 0,
    priority: null,
    discountPrice: 0,
    payingPrice: 0,
    priceInStruction: "",
    courseUpgradePrice: 0,
    metaTitle: "",
    metaHeading: "",
    metaDescription: [""],
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    language: Yup.string().required("Language is required"),
    courseMoneyBack: Yup.string().required("courseMoneyBack is required"),
    description: Yup.string().required("Description is required"),
    priority: Yup.string().required("Priority is required"),

    image: Yup.string().required("Image is required"),
    // syllabus: Yup.object().shape({
    // subjectLevel: Yup.string().required("Subject level is required"),
    // fullSyllabus: Yup.string().required("Full syllabus info is required"),
    // }),
    // totalLecture: Yup.number().min(0).required("Total lectures required"),
    // totalTest: Yup.number().min(0).required("Total tests required"),
    // price: Yup.number().min(0).required("Price required"),
    // discountPrice: Yup.number().min(0).required("Discount price required"),
    // payingPrice: Yup.number().min(0).required("Paying price required"),
    // priceInStruction: Yup.string().required("Price instruction required"),
    // courseUpgradePrice: Yup.number().min(0).required("Upgrade price required"),
  });

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      let response;
      if (courseId) {
        response = await dispatch(editCourse({ courseId, ...values }));
      } else {
        response = await dispatch(addCourse(values));
      }

      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        navigate(ROUTES.COURSES.BASE);
        setDescription("");
        resetForm();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setValues } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const getSingleRecord = useCallback(async () => {
    try {
      await dispatch(getSingleCourse(courseId));
    } catch (error) {}
  }, [dispatch, courseId]);

  useEffect(() => {
    if (courseId) {
      getSingleRecord();
    }
  }, [courseId, getSingleRecord]);

  useEffect(() => {
    if (courseId && getsingleCourseeData) {
      setValues({
        title: getsingleCourseeData?.title ?? "",
        image: getsingleCourseeData?.image ?? "",
        language: getsingleCourseeData?.language ?? "",
        syllabus: getsingleCourseeData?.syllabus ?? { subjectLevel: "" },
        courseMoneyBack: getsingleCourseeData?.courseMoneyBack ?? "",
        // totalLecture: getsingleCourseeData?.totalLecture ?? 0,
        // totalTest: getsingleCourseeData?.totalTest ?? 0,
        description: getsingleCourseeData?.description ?? "",
        pdf: getsingleCourseeData?.pdf ?? "",
        price: getsingleCourseeData?.price ?? 0,
        priority: getsingleCourseeData?.priority ?? 0,
        discountPrice: getsingleCourseeData?.discountPrice ?? 0,
        payingPrice: getsingleCourseeData?.payingPrice ?? 0,
        priceInStruction: getsingleCourseeData?.priceInStruction ?? "",
        courseUpgradePrice: getsingleCourseeData?.courseUpgradePrice ?? 0,
        metaTitle: getsingleCourseeData?.metaTitle ?? "",
        metaHeading: getsingleCourseeData?.metaHeading ?? "",
      });
      setDescription(getsingleCourseeData.description || "");
      setMetaDescription(getsingleCourseeData.metaDescription?.length ? getsingleCourseeData.metaDescription : [""]);
      setPreview(getsingleCourseeData.image);
      if (getsingleCourseeData.pdf) setPdfPreview(getsingleCourseeData.pdf);
    }
  }, [courseId, getsingleCourseeData, setValues]);

  const handleFileUpload = async (file, imageName) => {
    const formData = new FormData();
    formData.append("pdf", file);
    try {
      setLoading(true);
      const response = await dispatch(fileUpload(formData));
      if (response) {
        if (imageName === "image") {
          setFieldValue("image", response.payload);
        } else if (imageName === "pdf") {
          setFieldValue("pdf", response.payload);
        }
        setLoading(false);
      }
    } catch (error) {
      alert("Error uploading file. Please try again.");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setPdfPreview(fileURL);
      handleFileUpload(file, "pdf");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setPreview(imageURL);
      handleFileUpload(file, "image");
    }
  };

  const handleMetaDescriptionChange = (index, value) => {
    const updated = [...metaDescription];
    updated[index] = value;
    setMetaDescription(updated);
    setFieldValue("metaDescription", updated); // sync with Formik
  };

  const addMetaDescription = () => {
    const updated = [...metaDescription, ""];
    setMetaDescription(updated);
    setFieldValue("metaDescription", updated);
  };

  const removeMetaDescription = (index) => {
    const updated = metaDescription.filter((_, i) => i !== index);
    setMetaDescription(updated);
    setFieldValue("metaDescription", updated);
  };

  useEffect(() => {
    if (courseId && getsingleCourseeData?.description) {
      setDescription(getsingleCourseeData?.description);
    } else {
      setDescription("");
    }
  }, [courseId, getsingleCourseeData]);

  return (
    <div className="bg-background py-4 sm:py-4">
      <div className="px-4 md:py-10 py-5 bg-card shadow rounded-3xl sm:p-10">
        <p className="md:text-3xl text-xl text-center font-medium uppercase text-primary">{action} Course</p>
        <form className="mt-6" onSubmit={handleSubmit}>
          <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
            {/* Title */}
            <div>
              <label className="text-sm font-medium text-foreground">Title</label>
              <input type="text" name="title" className="w-full px-4 py-2 border border-border rounded-xl" value={values.title} onChange={handleChange} onBlur={handleBlur} placeholder="Course Title" />
              {touched.title && errors.title && <div className="text-red-500 text-sm">{errors.title}</div>}
            </div>

            {/* Language */}
            <div>
              <label className="text-sm font-medium text-foreground">Language</label>
              <input type="text" name="language" className="w-full px-4 py-2 border border-border rounded-xl" value={values.language} onChange={handleChange} onBlur={handleBlur} placeholder="Course Language" />
              {touched.language && errors.language && <div className="text-red-500 text-sm">{errors.language}</div>}
            </div>

            {/* Subject Level */}
            <div>
              <label className="text-sm font-medium text-foreground">Subject Level</label>
              <input type="text" name="syllabus.subjectLevel" className="w-full px-4 py-2 border border-border rounded-xl" value={values.syllabus.subjectLevel} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. Basic / Intermediate / Revision" />
            </div>

            {/* Full Syllabus */}
            {/* <div>
              <label className="text-sm font-medium text-foreground">Full Syllabus</label>
              <select name="syllabus.fullSyllabus" className="w-full px-4 py-2 border border-border rounded-xl" value={values.syllabus.fullSyllabus} onChange={handleChange}>
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div> */}

            {/* Course Money Back */}
            <div>
              <label className="text-sm font-medium text-foreground">Course Money Back</label>
              <input type="text" name="courseMoneyBack" className="w-full px-4 py-2 border border-border rounded-xl" value={values.courseMoneyBack} onChange={handleChange} onBlur={handleBlur} placeholder="Course Money Back" />
              {touched.courseMoneyBack && errors.courseMoneyBack && <div className="text-red-500 text-sm">{errors.courseMoneyBack}</div>}
            </div>

            {/* Total Lecture */}
            {/* <div>
              <label className="text-sm font-medium text-foreground">Total Lectures</label>
              <input type="number" name="totalLecture" className="w-full px-4 py-2 border border-border rounded-xl" value={values.totalLecture} onChange={handleChange} />
            </div> */}

            {/* Total Test */}
            {/* <div>
              <label className="text-sm font-medium text-foreground">Total Tests</label>
              <input type="number" name="totalTest" className="w-full px-4 py-2 border border-border rounded-xl" value={values.totalTest} onChange={handleChange} />
            </div> */}

            {/* Description */}
            {/* <div className="md:col-span-2">
              <label className="text-sm font-medium text-foreground">Description</label>
              <textarea name="description" className="w-full px-4 py-2 border border-border rounded-xl" rows="4" value={values.description} onChange={handleChange} />
              {touched.description && errors.description && <div className="text-red-500 text-sm">{errors.description}</div>}
            </div> */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-foreground">Description</label>

              <DescriptionEditor
                value={description}
                // onChange={setDescription}
                onChange={(content) => {
                  setDescription(content);
                  setValues({ ...values, description: content }); // ✅ update Formik too
                }}
                readOnly={false}
              />
              {touched.description && errors.description && <div className="text-red-500 text-sm">{errors.description}</div>}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Priority</label>
              <input type="number" name="priority" placeholder="0" className="w-full px-4 py-2 border border-border rounded-xl" value={values.priority} onChange={handleChange} />
              {touched.priority && errors.priority && <div className="text-red-500 text-sm">{errors.priority}</div>}
            </div>

            {/* Price fields */}
            <div>
              <label className="text-sm font-medium text-foreground">Price</label>
              <input type="number" name="price" className="w-full px-4 py-2 border border-border rounded-xl" value={values.price} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Discount Price</label>
              <input type="number" name="discountPrice" className="w-full px-4 py-2 border border-border rounded-xl" value={values.discountPrice} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Paying Price</label>
              <input type="number" name="payingPrice" className="w-full px-4 py-2 border border-border rounded-xl" value={values.payingPrice} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Price Instruction</label>
              <input type="text" name="priceInStruction" className="w-full px-4 py-2 border border-border rounded-xl" value={values.priceInStruction} onChange={handleChange} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Course Upgrade Price</label>
              <input type="number" name="courseUpgradePrice" className="w-full px-4 py-2 border border-border rounded-xl" value={values.courseUpgradePrice} onChange={handleChange} />
            </div>

            {/* Image Upload */}
            <div>
              <label className="text-sm font-medium text-foreground">Image</label>
              <input type="file" name="image" onChange={handleImageChange} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.image && errors.image && <div className="text-red-500 text-sm">{errors.image}</div>}

              {preview && (
                <div className="mt-4 flex items-center gap-3">
                  <p className="text-md text-foreground">Preview:</p>
                  <img src={preview} alt="Preview" className="w-20 h-20 object-cover rounded-xl border" />
                </div>
              )}
            </div>

            {/* PDF Upload */}
            <div>
              <label className="text-sm font-medium text-foreground">PDF</label>
              <input type="file" name="pdf" accept=".pdf" onChange={handleFileChange} className="w-full px-4 py-2 border border-border rounded-xl" />
              {pdfPreview && (
                <div className="mt-4 flex items-center">
                  <p className="text-md text-foreground mr-3">Preview:</p>
                  <Link to={pdfPreview} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary">
                    <AiFillFilePdf size={40} className="mr-2" />
                    View PDF
                  </Link>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Meta Title</label>
              <input type="text" name="metaTitle" className="w-full px-4 py-2 border border-border rounded-xl" value={values.metaTitle} onChange={handleChange} onBlur={handleBlur} placeholder="Course Meta Title" />
              {touched.metaTitle && errors.metaTitle && <div className="text-red-500 text-sm">{errors.metaTitle}</div>}
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Meta Heading</label>
              <input type="text" name="metaHeading" className="w-full px-4 py-2 border border-border rounded-xl" value={values.metaHeading} onChange={handleChange} onBlur={handleBlur} placeholder="Course Meta Heading" />
              {touched.metaHeading && errors.metaHeading && <div className="text-red-500 text-sm">{errors.metaHeading}</div>}
            </div>
            <div className="md:col-span-2">
              {metaDescription.map((item, index) => (
                <div key={index} className="flex gap-2 mt-2">
                  <div className="w-full">
                    <label className="text-sm font-medium text-foreground">Meta Description</label>
                    <input type="text" className="w-full px-4 py-2 border border-border rounded-xl" value={item} onChange={(e) => handleMetaDescriptionChange(index, e.target.value)} placeholder="Course Meta Description" />
                  </div>
                  {metaDescription.length > 1 && (
                    <button type="button" onClick={() => removeMetaDescription(index)} className="md:h-10 md:w-10 w-8 mt-6 flex justify-center items-center text-white bg-red-500 rounded-xl hover:bg-red-600">
                      <CgRemove className="text-sm sm:text-xl" />
                    </button>
                  )}

                  {index === metaDescription.length - 1 && (
                    <button type="button" onClick={addMetaDescription} className="md:h-10 md:w-10 w-8 mt-6 flex justify-center items-center text-white bg-primary rounded-xl">
                      <BiPlusCircle className="text-sm sm:text-2xl" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center">
            <button type="submit" className="w-full sm:w-[250px] min-h-[50px] flex justify-center items-center text-white bg-primary text-lg font-semibold rounded-xl hover:bg-accent mt-7">
              {loading ? <div className="loader"></div> : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseForm;
